import { ActionIcon, Select } from '@mantine/core';
import { IconTrash } from '@tabler/icons-react';
import { useSearchInvoices } from 'api';
import type { SearchInvoicesParams } from 'api/models';
import classNames from 'classnames/bind';
import { i18nKeys, useTranslation } from 'locales';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Amount from 'shared/components/Amount';
import { treatInvoice } from 'shared/serializer';
import type { LightInvoice } from 'types/store/invoice-state';

import { LightInvoiceItem } from '../../components';
import { useInvoiceFormContext } from '../../InvoiceForm.context';
import type { InvoiceFormSharedProps } from '../../InvoiceForm.types';

import styleIdentifiers from './InvoiceProductLine.module.scss';

const styles = classNames.bind(styleIdentifiers);

type SearchResultInvoice = LightInvoice & {
  total_htva: number;
};

type InvoiceProductLineInvoiceProps = InvoiceFormSharedProps & {
  remove: (index: number) => void;
  lineIndex: number;
  selected: number[];
};

export default function InvoiceProductLineInvoice(props: InvoiceProductLineInvoiceProps) {
  const { lineIndex, currency, remove, selected } = props;

  const { t } = useTranslation();
  const form = useInvoiceFormContext();

  const [params, setParams] = useState<SearchInvoicesParams>({ currency });
  const { data: invoicesResponse } = useSearchInvoices(params);

  const debtor = form.values.debtor_attributes;
  const productLine = form.values.product_lines_attributes?.[lineIndex];
  const totalHtva = productLine?.total_htva ?? 0;
  const totalTvac = productLine?.total_tvac ?? 0;

  useEffect(() => {
    if (Array.isArray(selected)) {
      setParams(currentParams => ({
        ...currentParams,
        exclude: selected.filter(e => Number(e) !== Number(productLine?.linked_invoice_id)),
      }));
    }
  }, [productLine, selected]);

  useEffect(() => {
    if (debtor) {
      setParams(currentParams => ({
        ...currentParams,
        debtor_id: debtor.id,
      }));
    }
  }, [debtor]);

  const invoices = useMemo(
    () =>
      (invoicesResponse?.data ?? []).map(e => treatInvoice(e) as unknown as SearchResultInvoice),
    [invoicesResponse?.data],
  );

  const selectData = useMemo(
    () =>
      invoices.map(invoice => ({
        value: String(invoice.id),
        label: invoice.reference,
      })),
    [invoices],
  );

  const setFieldValue = form.setFieldValue;

  const handleChange = useCallback(
    (selectedId: string | null) => {
      if (!selectedId) return;
      const invoice = invoices.find(inv => String(inv.id) === selectedId);
      if (!invoice) return;

      setFieldValue(`product_lines_attributes.${lineIndex}.linked_invoice_id`, Number(selectedId));
      setFieldValue(
        `product_lines_attributes.${lineIndex}.product_attributes.name`,
        invoice.reference,
      );
      setFieldValue(
        `product_lines_attributes.${lineIndex}.product_attributes.current_unity_price_htva`,
        -invoice.total_htva,
      );
      setFieldValue(`product_lines_attributes.${lineIndex}.total_htva`, -invoice.total_htva);
      setFieldValue(`product_lines_attributes.${lineIndex}.total_tvac`, -invoice.total_tvac);
    },
    [invoices, lineIndex, setFieldValue],
  );

  return (
    <tr className={styles('invoice-product-line')}>
      <td>
        <Select
          data={selectData}
          value={productLine?.linked_invoice_id ? String(productLine.linked_invoice_id) : null}
          onChange={handleChange}
          searchable
          filter={({ options }) => options}
          placeholder={t(i18nKeys.INVOICING.CREATE.CHOOSE_PRODUCT)}
          className={styles('invoice-select-input-wrapper')}
          size="xs"
          renderOption={({ option }) => {
            const invoice = invoices.find(inv => String(inv.id) === option.value);
            if (!invoice) return option.label;
            return <LightInvoiceItem item={invoice} currency={currency} />;
          }}
        />
      </td>
      <td />
      <td>
        <Amount value={totalHtva} suffix={currency} />
      </td>
      <td />
      <td className={styles('amount')}>
        <div className={styles('htva-price')}>
          <Amount value={totalHtva} suffix={currency} />
        </div>
      </td>
      <td />
      <td className={styles('amount')}>
        <div className={styles('htva-price')}>
          <Amount value={totalTvac} suffix={currency} />
        </div>
      </td>
      <td>
        <ActionIcon variant="subtle" color="red" onClick={() => remove(lineIndex)}>
          <IconTrash size={16} stroke={1.5} />
        </ActionIcon>
      </td>
    </tr>
  );
}
